import * as React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import get from 'lodash/get';

import ProductCard, {
  CardImage,
  CardSubSubTitle,
} from '../../components/ProductCard/ProductCard';
import { Paragraph, H3 } from '../../components/Typography';
import Button from '../../components/Button';

import Breakpoints from '../../design-utils/breakpoints';
import useAddonsContext from './AddonsContext';
import { useGlobalContext } from '../../subscriptions/state/GlobalContext';
import { useAddonsModal, AddonProduct, addonIsBaB } from './useAddons';
import AddonSlidePanel from './AddonSlidePanel';
import { AddonAddBundleModal } from './AddonAddBundleModal';
import { showMinMaxPrice } from '../utils';
import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
} from '../../../widgetSrc/utils/featureFlags';

const FEATURED_LIMIT = 3;
const CURRENCY = get(window, ['Shopify', 'currency', 'active'], 'USD');

const Addons = (): JSX.Element | null => {
  const { t } = useTranslation();

  const {
    addonProducts,
    isLoading,
    selectAddon,
    selectedBundle,
    handleSaveBundle,
    closePanel,
    subscription,
    sellingPlans,
    shopConfiguration,
  } = useAddonsContext();
  const { ignoreBaBOutOfStockAllowance } = useGlobalContext();

  const isBaBIgnoreOutOfStockEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.LAUNCH_BAB_IGNORE_OUT_OF_STOCK,
  );

  const { openModal, closeModal } = useAddonsModal(
    t('addons.modal_title'),
    selectAddon,
    addonProducts,
  );

  let addonsBody;
  if (isLoading)
    addonsBody = (
      <Paragraph modifiers={['body', 'subdued']}>Loading...</Paragraph>
    );
  else
    addonsBody = !isEmpty(addonProducts) ? (
      <>
        <AddonsHeader>
          <div>
            <H3 modifiers={['heading']}>{t('addons.title')}</H3>
            <Paragraph modifiers={['body']}>{t('addons.subtitle')}</Paragraph>
          </div>
          <Button onClick={openModal} outlined>
            {t('addons.see_products')}
          </Button>
        </AddonsHeader>
        <AddonsContainer>
          {addonProducts
            .slice(0, FEATURED_LIMIT)
            .map(({ productData: addon }: AddonProduct) => {
              const ignoreOutOfStock =
                isBaBIgnoreOutOfStockEnabled &&
                ignoreBaBOutOfStockAllowance &&
                addon.variants.every(
                  (variant) =>
                    variant.inventory &&
                    variant.inventory?.inventoryPolicy === 'continue' &&
                    variant.inventory?.inventoryManagement === 'shopify' &&
                    variant.inventory?.inventoryQuantity <= 0,
                );

              return (
                <ProductCard key={addon.id} id={`${addon.id}`}>
                  <CardImage
                    image={addon.image}
                    alt={`${addon.title}: ${addon.description}`}
                  />
                  <CardSubSubTitle
                    className="bundle-product-card__subtitle"
                    modifiers={['body']}
                  >
                    {addon.title}
                  </CardSubSubTitle>

                  <Button
                    fullWidth
                    filled
                    onClick={() =>
                      selectAddon(parseInt(`${addon.id}`), addonIsBaB(addon))
                    }
                    disabled={!addon.available || ignoreOutOfStock}
                  >
                    {addon.available && !ignoreOutOfStock
                      ? addonIsBaB(addon)
                        ? t('product_swap.customize_bundle')
                        : t('addons.select')
                      : t('product_swap.sold_out')}
                  </Button>
                  <Paragraph
                    modifiers={{
                      body: true,
                      subdued: !!addon.discountedPrice,
                    }}
                    style={{
                      marginTop: 20,
                      textDecoration: addon.discountedPrice
                        ? 'line-through'
                        : undefined,
                    }}
                  >
                    {parseInt(addon.priceMax as string) === 0 &&
                    parseInt(addon.priceMin as string) === 0
                      ? null
                      : showMinMaxPrice({
                          currency: addon.priceCurrency || CURRENCY,
                          max: addon.priceMax as number,
                          min: addon.priceMin as number,
                        })}
                  </Paragraph>

                  {!isNull(addon.discountedPrice) ||
                  !isNaN(addon.discountedPrice) ? (
                    <DiscountedPrice>
                      {showMinMaxPrice({
                        currency: addon.priceCurrency || CURRENCY,
                        max: addon.discountedPriceMax as number,
                        min: addon.discountedPriceMin as number,
                      })}
                    </DiscountedPrice>
                  ) : null}
                </ProductCard>
              );
            })}
        </AddonsContainer>

        <AddonSlidePanel closeModal={closeModal} />
        <AddonAddBundleModal
          selectedBundle={selectedBundle}
          handleSaveBundle={handleSaveBundle}
          closePanel={closePanel}
          subscription={subscription}
          sellingPlans={sellingPlans}
          shopConfiguration={shopConfiguration}
        />
      </>
    ) : null;

  return <div className="addon-container">{addonsBody}</div>;
};

const AddonsHeader = styled.header`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 40px;
  @media (max-width: ${Breakpoints.lg}) {
    button {
      margin-top: 1rem;
    }
  }
`;

const AddonsContainer = styled.div`
  margin: 20px 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, 200px);
  grid-gap: 30px;
  justify-content: space-between;

  @media screen and (max-width: ${Breakpoints.md}) {
    grid-template-columns: repeat(auto-fit, 240px);
  }

  @media (max-width: ${Breakpoints.sm}) {
    justify-content: center;
  }
`;

const DiscountedPrice = styled.span.attrs({
  className: 'discounted-addons-featured-price',
})`
  font-weight: 600;
  font-size: 16px;
`;

export default Addons;
